import React, { useState, useEffect } from 'react';
import { 
  Heart, Shield, Clock, Video, FileText, Users, Star, CheckCircle, 
  ArrowRight, Calendar, Activity, Target, TrendingUp, Zap,
  Apple, Moon, Brain, Stethoscope, Award, Phone, MessageCircle,
  LineChart, Pill, Dumbbell, Salad, HeartPulse, Scale, Thermometer,
  Droplets, ChevronRight, Play
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LifestyleDiseases = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [activeProgram, setActiveProgram] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lifestyle Diseases Data
  const lifestyleDiseases = [
    { name: 'Diabetes', icon: '🩸', prevalence: '77M+ affected in India' },
    { name: 'Hypertension', icon: '❤️‍🩹', prevalence: '1 in 4 adults' },
    { name: 'Obesity', icon: '⚖️', prevalence: 'Rising epidemic' },
    { name: 'Heart Disease', icon: '🫀', prevalence: '#1 cause of death' },
    { name: 'High Cholesterol', icon: '🧪', prevalence: '25% of adults' },
    { name: 'Thyroid Disorders', icon: '🦋', prevalence: '42M+ Indians' },
    { name: 'PCOS', icon: '🌸', prevalence: '1 in 5 women' },
    { name: 'Sleep Disorders', icon: '😴', prevalence: '93% sleep deprived' }
  ];

  // Comprehensive Services
  const services = [
    {
      icon: Video,
      title: 'Digital Consultations',
      description: 'Connect with specialist doctors via HD video calls. Get expert advice without leaving home.',
      color: '#1D57D5'
    },
    {
      icon: LineChart,
      title: 'Continuous Monitoring',
      description: 'Track your vitals, medications, and progress with our integrated health dashboard.',
      color: '#28C08A'
    },
    {
      icon: Salad,
      title: 'Diet & Nutrition Counseling',
      description: 'Personalized meal plans from certified nutritionists tailored to your health goals.',
      color: '#22C55E'
    },
    {
      icon: Pill,
      title: 'Medication Management',
      description: 'Smart reminders, refill alerts, and drug interaction checks to keep you safe.',
      color: '#8B5CF6'
    },
    {
      icon: Dumbbell,
      title: 'Exercise & Lifestyle Coaching',
      description: 'Custom fitness plans designed around your condition, age, and fitness level.',
      color: '#F97316'
    },
    {
      icon: Thermometer,
      title: 'Lab Tests & Screenings',
      description: 'Home sample collection for 500+ diagnostic tests with digital reports.',
      color: '#EF4444'
    },
    {
      icon: Shield,
      title: 'Preventive Health Strategies',
      description: 'Early warning systems and risk assessments to catch problems before they start.',
      color: '#0EA5E9'
    },
    {
      icon: Brain,
      title: 'AI-Powered Risk Assessment',
      description: 'Our Health Genie analyzes your data to predict and prevent health issues.',
      color: '#EC4899'
    }
  ];

  // Health Programs
  const programs = [
    {
      id: 'diabetes',
      title: 'Diabetes Care Program',
      subtitle: 'Comprehensive Type 1 & Type 2 Management',
      icon: '🩸',
      color: '#EF4444',
      description: 'A 360° approach to diabetes management combining continuous glucose monitoring, personalized diet plans, medication optimization, and lifestyle modifications.',
      features: [
        'HbA1c tracking & optimization',
        'Personalized insulin/medication management',
        'Diabetic diet planning with Indian cuisine options',
        'Foot care & complication prevention',
        'Stress & diabetes correlation coaching',
        '24/7 doctor access for emergencies'
      ],
      outcomes: ['Average HbA1c reduction: 1.5%', '78% improved quality of life', 'Reduced medication dependency']
    },
    {
      id: 'heart',
      title: 'Heart & Hypertension Management',
      subtitle: 'Cardiovascular Health Excellence',
      icon: '🫀',
      color: '#DC2626',
      description: 'Protect your heart with our comprehensive cardiac care program. From BP monitoring to stress management, we cover everything for a healthier heart.',
      features: [
        'Daily BP tracking & alerts',
        'Heart-healthy diet consultation',
        'Cardiac risk score monitoring',
        'Stress & anger management',
        'Exercise prescription for heart health',
        'Medication adherence support'
      ],
      outcomes: ['Average BP reduction: 15-20 mmHg', '65% reduced cardiac events', 'Improved energy levels']
    },
    {
      id: 'weight',
      title: 'Weight Loss & Metabolic Reset',
      subtitle: 'Sustainable Weight Management',
      icon: '⚖️',
      color: '#F97316',
      description: 'No crash diets. No quick fixes. A medically supervised program that addresses the root causes of weight gain and metabolic dysfunction.',
      features: [
        'Metabolic rate assessment',
        'Personalized calorie & macro planning',
        'Behavioral therapy for eating habits',
        'Thyroid & hormone optimization',
        'Weekly check-ins with specialists',
        'Body composition tracking'
      ],
      outcomes: ['Average weight loss: 8-12 kg in 6 months', '85% maintained weight at 1 year', 'Improved metabolic markers']
    },
    {
      id: 'thyroid',
      title: 'Thyroid Balance Program',
      subtitle: 'Hypo & Hyperthyroid Management',
      icon: '🦋',
      color: '#8B5CF6',
      description: 'Comprehensive thyroid care addressing fatigue, weight issues, mood changes, and hormonal imbalances with precision medicine approaches.',
      features: [
        'Complete thyroid panel monitoring',
        'Medication dose optimization',
        'Thyroid-specific nutrition plans',
        'Energy & mood tracking',
        'Hair & skin health support',
        'Pregnancy planning guidance'
      ],
      outcomes: ['Normalized TSH in 90% patients', 'Significant energy improvement', 'Better weight management']
    },
    {
      id: 'womens',
      title: 'Women\'s Hormonal Health',
      subtitle: 'PCOS, Menopause & Beyond',
      icon: '🌸',
      color: '#EC4899',
      description: 'Specialized care for PCOS, irregular periods, fertility concerns, and menopause. Holistic management for every stage of womanhood.',
      features: [
        'Hormonal profile assessment',
        'PCOS reversal protocols',
        'Fertility optimization',
        'Menopause symptom management',
        'Bone health monitoring',
        'Mental wellness support'
      ],
      outcomes: ['70% PCOS symptom reduction', 'Improved fertility outcomes', 'Better menopause transition']
    },
    {
      id: 'stress',
      title: 'Stress & Sleep Health',
      subtitle: 'Mental Wellness for Physical Health',
      icon: '🧠',
      color: '#0EA5E9',
      description: 'Address the hidden drivers of lifestyle diseases—chronic stress, anxiety, and poor sleep. Restore balance for better health outcomes.',
      features: [
        'Sleep quality assessment',
        'Stress biomarker tracking',
        'Cognitive behavioral therapy',
        'Mindfulness & meditation guidance',
        'Work-life balance coaching',
        'Sleep hygiene optimization'
      ],
      outcomes: ['60% better sleep quality', 'Reduced cortisol levels', 'Improved overall health markers']
    },
    {
      id: 'aging',
      title: 'Healthy Aging Program',
      subtitle: 'Vitality at Every Age',
      icon: '🌟',
      color: '#14B8A6',
      description: 'Age gracefully with proactive health management. From bone density to cognitive health, we support every aspect of healthy aging.',
      features: [
        'Comprehensive geriatric assessment',
        'Bone density & muscle health',
        'Cognitive function monitoring',
        'Cardiovascular screening',
        'Vision & hearing care coordination',
        'Fall prevention strategies'
      ],
      outcomes: ['Maintained independence', 'Reduced hospitalization', 'Better quality of life']
    },
    {
      id: 'cholesterol',
      title: 'Cholesterol & Lipid Management',
      subtitle: 'Heart-Protective Lipid Control',
      icon: '🧪',
      color: '#6366F1',
      description: 'Beyond just lowering numbers—understand your lipid profile and implement lifestyle changes for lasting cardiovascular protection.',
      features: [
        'Advanced lipid panel testing',
        'Statin optimization & alternatives',
        'Anti-inflammatory diet plans',
        'Exercise for lipid health',
        'Genetic risk assessment',
        'Regular progress monitoring'
      ],
      outcomes: ['30% LDL reduction on average', 'Improved HDL/LDL ratio', 'Reduced cardiovascular risk']
    }
  ];

  // Why GEWO Differentiators
  const differentiators = [
    {
      icon: Award,
      title: 'Verified Medical Experts',
      description: 'Every doctor is board-certified with 10+ years average experience in lifestyle medicine.',
      stat: '10,000+ Specialists'
    },
    {
      icon: LineChart,
      title: 'Technology-Driven Monitoring',
      description: 'AI-powered dashboards track your progress and alert doctors to concerning trends.',
      stat: 'Real-Time Insights'
    },
    {
      icon: Shield,
      title: 'Secure Health Records',
      description: 'HIPAA-compliant, encrypted storage keeps your medical data safe and accessible.',
      stat: 'Bank-Level Security'
    },
    {
      icon: Target,
      title: 'Early Detection Focus',
      description: 'Predictive analytics identify risks before they become problems.',
      stat: '89% Early Detection Rate'
    },
    {
      icon: Users,
      title: 'Personalized Care Plans',
      description: 'No cookie-cutter solutions. Every plan is tailored to your unique health profile.',
      stat: '100% Customized'
    },
    {
      icon: Video,
      title: 'Easy Access via App',
      description: 'Consult doctors, track health, and manage medications—all from your phone.',
      stat: '60-Second Connection'
    },
    {
      icon: Clock,
      title: '24/7 Continuous Care',
      description: 'Round-the-clock access to medical support whenever you need it.',
      stat: 'Always Available'
    }
  ];

  // Success Stories
  const successStories = [
    {
      name: 'Rajesh Mehta',
      age: 52,
      location: 'Mumbai',
      condition: 'Type 2 Diabetes',
      quote: 'In 6 months with GEWO, my HbA1c dropped from 9.2 to 6.8. My doctor back home couldn\'t believe the transformation. The combination of personalized diet plans and regular specialist check-ins made all the difference. I feel like I\'ve been given a second chance at life.',
      outcome: 'HbA1c: 9.2% → 6.8%',
      duration: '6 months'
    },
    {
      name: 'Priya Sharma',
      age: 38,
      location: 'Delhi',
      condition: 'PCOS & Weight Management',
      quote: 'After struggling with PCOS for 8 years, I finally found a program that understood my body. Lost 18 kgs, my periods are regular for the first time in years, and I conceived naturally after being told I might need IVF. GEWO changed my life completely.',
      outcome: 'Lost 18 kg, Natural Conception',
      duration: '10 months'
    },
    {
      name: 'Amit Kapoor',
      age: 45,
      location: 'Bangalore',
      condition: 'Hypertension & Stress',
      quote: 'Running a startup meant my BP was always sky-high. GEWO\'s stress management program combined with medication optimization brought my readings from 160/100 to 125/80. I sleep better, work better, and my family notices the difference.',
      outcome: 'BP: 160/100 → 125/80',
      duration: '4 months'
    }
  ];

  // Target Audience Data
  const targetAudience = [
    {
      icon: '💼',
      title: 'Working Professionals',
      description: 'Busy executives who need flexible, efficient healthcare that fits their schedule.'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Busy Parents',
      description: 'Managing family health while juggling work and responsibilities.'
    },
    {
      icon: '🎯',
      title: 'Adults 25-60',
      description: 'Proactive individuals who want to prevent lifestyle diseases before they start.'
    },
    {
      icon: '❤️',
      title: 'Caregivers',
      description: 'Those caring for aging parents or family members with chronic conditions.'
    },
    {
      icon: '⚠️',
      title: 'At-Risk Individuals',
      description: 'Family history of diabetes, heart disease, or other chronic conditions.'
    },
    {
      icon: '🔄',
      title: 'Chronic Condition Managers',
      description: 'Already diagnosed and seeking better, more comprehensive care.'
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* ========== HERO SECTION ========== */}
      <section style={{
        background: 'linear-gradient(135deg, #1E3A5F 0%, #0F172A 50%, #1E3A5F 100%)',
        minHeight: isMobile ? 'auto' : '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: isSmallMobile ? '100px 16px 60px' : isMobile ? '100px 20px 80px' : '120px 40px 100px'
      }}>
        {/* Animated Medical Grid Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.08) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          opacity: 0.5
        }} />

        {/* Floating Health Icons */}
        {!isSmallMobile && (
          <>
            <div style={{
              position: 'absolute',
              top: '15%',
              left: '8%',
              fontSize: '48px',
              opacity: 0.15,
              animation: 'float 8s ease-in-out infinite'
            }}>🫀</div>
            <div style={{
              position: 'absolute',
              top: '60%',
              right: '5%',
              fontSize: '40px',
              opacity: 0.15,
              animation: 'float 6s ease-in-out infinite 1s'
            }}>🩺</div>
            <div style={{
              position: 'absolute',
              bottom: '20%',
              left: '15%',
              fontSize: '36px',
              opacity: 0.15,
              animation: 'float 7s ease-in-out infinite 2s'
            }}>💊</div>
          </>
        )}

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(249, 115, 22, 0.15)',
            border: '2px solid rgba(249, 115, 22, 0.4)',
            borderRadius: '50px',
            padding: isSmallMobile ? '8px 16px' : '12px 24px',
            marginBottom: isSmallMobile ? '24px' : '32px',
            backdropFilter: 'blur(10px)',
            marginTop: '40px',
          }}>
            <HeartPulse size={isSmallMobile ? 16 : 20} color="#F97316" />
            <span style={{
              color: '#F97316',
              fontSize: isSmallMobile ? '12px' : '14px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>Long-Term Health Excellence</span>
          </div>

          {/* Main Headline */}
          <h1 style={{
            fontSize: isSmallMobile ? '32px' : isMobile ? '42px' : '60px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.1',
            letterSpacing: '-1px',
            maxWidth: '1000px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '16px' : '24px'
          }}>
            Take Control of{' '}
            <span style={{
              background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Lifestyle Diseases
            </span>
            {' '}Before They Control You
          </h1>

          {/* Subheadline */}
          <p style={{
            fontSize: isSmallMobile ? '16px' : isMobile ? '18px' : '22px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '750px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '24px' : '36px',
            padding: '0 8px'
          }}>
            Diabetes. Hypertension. Obesity. Heart disease. <strong>They're preventable. They're manageable. 
            They're even reversible.</strong> Start your personalized health journey with India's most trusted doctors.
          </p>

          {/* Trust Statement */}
          <p style={{
            fontSize: isSmallMobile ? '13px' : '15px',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '32px' : '40px'
          }}>
            🏥 10,000+ Verified Specialists • 📊 AI-Powered Health Tracking • 🔒 100% Private & Secure
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '16px',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: isSmallMobile ? '48px' : '64px'
          }}>
            <button style={{
              background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              color: 'white',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '18px 36px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(249, 115, 22, 0.35)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Target size={20} />
              Start Your Health Plan
            </button>

            <button style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '18px 36px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Video size={20} />
              Talk to a Specialist
            </button>
          </div>

          {/* Condition Tags */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {lifestyleDiseases.map((disease, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '50px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  fontSize: isSmallMobile ? '12px' : '14px',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}
              >
                <span>{disease.icon}</span>
                <span>{disease.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHAT ARE LIFESTYLE DISEASES ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '40px' : '80px',
            alignItems: 'center'
          }}>
            {/* Left Content */}
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                border: '2px solid #F59E0B',
                borderRadius: '50px',
                padding: isSmallMobile ? '8px 16px' : '10px 24px',
                marginBottom: isSmallMobile ? '16px' : '24px'
              }}>
                <Activity size={isSmallMobile ? 14 : 18} color="#D97706" />
                <span style={{
                  color: '#D97706',
                  fontSize: isSmallMobile ? '11px' : '14px',
                  fontWeight: '700',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>Understanding the Challenge</span>
              </div>

              <h2 style={{
                fontSize: isSmallMobile ? '28px' : isMobile ? '34px' : '44px',
                fontWeight: '800',
                color: '#1A1A2E',
                marginBottom: '20px',
                lineHeight: '1.2'
              }}>
                What Are{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #F97316 0%, #DC2626 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Lifestyle Diseases?</span>
              </h2>

              <p style={{
                fontSize: isSmallMobile ? '15px' : '17px',
                color: '#4B5563',
                lineHeight: '1.8',
                marginBottom: '20px'
              }}>
                Lifestyle diseases are chronic conditions primarily caused by <strong>how we live</strong>—our diet, 
                physical activity, stress levels, and daily habits. Unlike infectious diseases, they develop 
                slowly over years and are directly linked to modern life patterns.
              </p>

              <p style={{
                fontSize: isSmallMobile ? '15px' : '17px',
                color: '#4B5563',
                lineHeight: '1.8',
                marginBottom: '24px'
              }}>
                The good news? <strong>Up to 80% of heart disease, stroke, and type 2 diabetes cases are preventable</strong> 
                with the right lifestyle modifications and medical guidance.
              </p>

              <div style={{
                background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                borderRadius: '16px',
                padding: isSmallMobile ? '20px' : '24px',
                border: '2px solid #FDBA74'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <TrendingUp size={24} color="#EA580C" />
                  <span style={{ fontSize: isSmallMobile ? '16px' : '18px', fontWeight: '700', color: '#9A3412' }}>
                    The Growing Crisis
                  </span>
                </div>
                <p style={{ fontSize: isSmallMobile ? '14px' : '15px', color: '#7C2D12', lineHeight: '1.6' }}>
                  India is facing an epidemic: <strong>77 million diabetics</strong>, <strong>200+ million with hypertension</strong>, 
                  and <strong>heart disease now affecting people in their 30s</strong>. But with the right care, 
                  these numbers can change—starting with you.
                </p>
              </div>
            </div>

            {/* Right - Stats Grid */}
            <div style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              {[
                { stat: '77M+', label: 'Indians with Diabetes', color: '#EF4444' },
                { stat: '200M+', label: 'With Hypertension', color: '#DC2626' },
                { stat: '80%', label: 'Cases Preventable', color: '#22C55E' },
                { stat: '40%', label: 'Under 45 at Risk', color: '#F97316' }
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: '#FAFAFA',
                    borderRadius: '16px',
                    padding: isSmallMobile ? '20px' : '28px',
                    textAlign: 'center',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{
                    fontSize: isSmallMobile ? '32px' : '42px',
                    fontWeight: '900',
                    color: item.color,
                    marginBottom: '8px'
                  }}>{item.stat}</div>
                  <div style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: '#6B7280',
                    fontWeight: '500'
                  }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHO IS THIS FOR ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
              border: '2px solid #0EA5E9',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Users size={isSmallMobile ? 14 : 18} color="#0369A1" />
              <span style={{
                color: '#0369A1',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Is This For You?</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Who Benefits from{' '}
              <span style={{
                background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>GEWO's Programs?</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '650px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Whether you're preventing, managing, or reversing lifestyle diseases—GEWO 
              provides the expertise and technology you need.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '16px' : '24px'
          }}>
            {targetAudience.map((audience, index) => (
              <div
                key={index}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '20px',
                  padding: isSmallMobile ? '24px 20px' : '32px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #E5E7EB',
                  transition: 'all 0.3s ease'
                }}
              >
                <span style={{ fontSize: isSmallMobile ? '36px' : '44px', display: 'block', marginBottom: '16px' }}>
                  {audience.icon}
                </span>
                <h3 style={{
                  fontSize: isSmallMobile ? '18px' : '20px',
                  fontWeight: '700',
                  color: '#1A1A2E',
                  marginBottom: '8px'
                }}>{audience.title}</h3>
                <p style={{
                  fontSize: isSmallMobile ? '14px' : '15px',
                  color: '#6B7280',
                  lineHeight: '1.6'
                }}>{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== COMPREHENSIVE SERVICES ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
              border: '2px solid #22C55E',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Stethoscope size={isSmallMobile ? 14 : 18} color="#16A34A" />
              <span style={{
                color: '#16A34A',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Comprehensive Care</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Everything You Need for{' '}
              <span style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Better Health</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '650px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              A complete ecosystem of services designed to prevent, manage, and reverse 
              lifestyle diseases through expert care and advanced technology.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isSmallMobile ? '16px' : '24px'
          }}>
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: '#FAFAFA',
                    borderRadius: '20px',
                    padding: isSmallMobile ? '24px 20px' : '28px',
                    border: '1px solid #E5E7EB',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{
                    width: isSmallMobile ? '48px' : '56px',
                    height: isSmallMobile ? '48px' : '56px',
                    borderRadius: '14px',
                    background: `${service.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <Icon size={isSmallMobile ? 24 : 28} color={service.color} />
                  </div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: '#1A1A2E',
                    marginBottom: '8px'
                  }}>{service.title}</h3>
                  <p style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: '#6B7280',
                    lineHeight: '1.6'
                  }}>{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== CONDITION-SPECIFIC PROGRAMS ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: 'linear-gradient(135deg, #1E3A5F 0%, #0F172A 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(249, 115, 22, 0.15)',
              border: '2px solid rgba(249, 115, 22, 0.4)',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Target size={isSmallMobile ? 14 : 18} color="#F97316" />
              <span style={{
                color: '#F97316',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Specialized Programs</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#FFFFFF',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Condition-Specific{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Health Programs</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: 'rgba(255, 255, 255, 0.7)',
              maxWidth: '650px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Clinically designed programs targeting specific conditions with 
              measurable outcomes and personalized care paths.
            </p>
          </div>

          {/* Program Tabs */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '24px'
          }}>
            {/* Program Selector */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              gap: '8px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: isMobile ? 'center' : 'flex-start',
              minWidth: isMobile ? 'auto' : '280px'
            }}>
              {programs.map((program, index) => (
                <button
                  key={index}
                  onClick={() => setActiveProgram(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: isSmallMobile ? '10px 14px' : '14px 20px',
                    background: activeProgram === index ? program.color : 'rgba(255, 255, 255, 0.05)',
                    color: activeProgram === index ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    border: `2px solid ${activeProgram === index ? program.color : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: '600',
                    fontSize: isSmallMobile ? '12px' : '14px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{program.icon}</span>
                  {!isSmallMobile && program.title.split(' ')[0]}
                </button>
              ))}
            </div>

            {/* Active Program Content */}
            <div style={{
              flex: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: isSmallMobile ? '28px 20px' : '40px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <span style={{ fontSize: isSmallMobile ? '40px' : '56px' }}>
                  {programs[activeProgram].icon}
                </span>
                <div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '22px' : '28px',
                    fontWeight: '800',
                    color: '#FFFFFF',
                    marginBottom: '4px'
                  }}>{programs[activeProgram].title}</h3>
                  <span style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: programs[activeProgram].color,
                    fontWeight: '600'
                  }}>{programs[activeProgram].subtitle}</span>
                </div>
              </div>

              <p style={{
                fontSize: isSmallMobile ? '15px' : '16px',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.7',
                marginBottom: '24px'
              }}>{programs[activeProgram].description}</p>

              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '16px',
                padding: isSmallMobile ? '20px' : '24px',
                marginBottom: '24px'
              }}>
                <h4 style={{
                  fontSize: isSmallMobile ? '14px' : '15px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>What's Included</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isSmallMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '12px'
                }}>
                  {programs[activeProgram].features.map((feature, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: isSmallMobile ? '13px' : '14px',
                        color: 'rgba(255, 255, 255, 0.8)'
                      }}
                    >
                      <CheckCircle size={16} color={programs[activeProgram].color} />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcomes */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {programs[activeProgram].outcomes.map((outcome, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '10px 18px',
                      background: `${programs[activeProgram].color}20`,
                      borderRadius: '50px',
                      fontSize: isSmallMobile ? '12px' : '13px',
                      color: programs[activeProgram].color,
                      fontWeight: '600'
                    }}
                  >
                    ✓ {outcome}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== WHY GEWO ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
              border: '2px solid #F59E0B',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <Star size={isSmallMobile ? 14 : 18} color="#D97706" fill="#D97706" />
              <span style={{
                color: '#D97706',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>The GEWO Advantage</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Why GEWO for{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Lifestyle Disease Management?</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '650px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              We combine medical expertise with cutting-edge technology to deliver 
              healthcare that's effective, accessible, and truly personalized.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isSmallMobile ? '16px' : '20px'
          }}>
            {differentiators.slice(0, 4).map((diff, index) => {
              const Icon = diff.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%)',
                    borderRadius: '20px',
                    padding: isSmallMobile ? '24px 20px' : '28px',
                    textAlign: 'center',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Icon size={28} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: '#1A1A2E',
                    marginBottom: '8px'
                  }}>{diff.title}</h3>
                  <p style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: '#6B7280',
                    lineHeight: '1.5',
                    marginBottom: '16px'
                  }}>{diff.description}</p>
                  <div style={{
                    fontSize: isSmallMobile ? '12px' : '13px',
                    fontWeight: '700',
                    color: '#F97316',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>{diff.stat}</div>
                </div>
              );
            })}
          </div>

          {/* Second Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '16px' : '20px',
            marginTop: '20px'
          }}>
            {differentiators.slice(4).map((diff, index) => {
              const Icon = diff.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%)',
                    borderRadius: '20px',
                    padding: isSmallMobile ? '24px 20px' : '28px',
                    textAlign: 'center',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1D57D5 0%, #1E40AF 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Icon size={28} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: isSmallMobile ? '16px' : '18px',
                    fontWeight: '700',
                    color: '#1A1A2E',
                    marginBottom: '8px'
                  }}>{diff.title}</h3>
                  <p style={{
                    fontSize: isSmallMobile ? '13px' : '14px',
                    color: '#6B7280',
                    lineHeight: '1.5',
                    marginBottom: '16px'
                  }}>{diff.description}</p>
                  <div style={{
                    fontSize: isSmallMobile ? '12px' : '13px',
                    fontWeight: '700',
                    color: '#1D57D5',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>{diff.stat}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SUCCESS STORIES ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isSmallMobile ? '40px' : '64px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)',
              border: '2px solid #22C55E',
              borderRadius: '50px',
              padding: isSmallMobile ? '8px 16px' : '10px 24px',
              marginBottom: isSmallMobile ? '16px' : '24px'
            }}>
              <MessageCircle size={isSmallMobile ? 14 : 18} color="#16A34A" />
              <span style={{
                color: '#16A34A',
                fontSize: isSmallMobile ? '11px' : '14px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>Real Results</span>
            </div>

            <h2 style={{
              fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: '#1A1A2E',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Success Stories from{' '}
              <span style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Real Patients</span>
            </h2>

            <p style={{
              fontSize: isSmallMobile ? '15px' : '18px',
              color: '#6B7280',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Thousands have transformed their health with GEWO. 
              Here are just a few of their stories.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isSmallMobile ? '20px' : '32px'
          }}>
            {successStories.map((story, index) => (
              <div
                key={index}
                style={{
                  background: '#FFFFFF',
                  borderRadius: '24px',
                  padding: isSmallMobile ? '28px 24px' : '36px',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                  border: '1px solid #E5E7EB',
                  position: 'relative'
                }}
              >
                {/* Outcome Badge */}
                <div style={{
                  position: 'absolute',
                  top: isSmallMobile ? '20px' : '28px',
                  right: isSmallMobile ? '20px' : '28px',
                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: '50px',
                  fontSize: isSmallMobile ? '11px' : '12px',
                  fontWeight: '700'
                }}>
                  {story.outcome}
                </div>

                {/* Quote */}
                <p style={{
                  fontSize: isSmallMobile ? '15px' : '16px',
                  color: '#4B5563',
                  lineHeight: '1.7',
                  marginBottom: '24px',
                  fontStyle: 'italic',
                  paddingTop: '24px'
                }}>"{story.quote}"</p>

                {/* Author */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {story.name.charAt(0)}
                  </div>
                  <div>
                    <p style={{
                      fontSize: isSmallMobile ? '15px' : '16px',
                      fontWeight: '700',
                      color: '#1A1A2E'
                    }}>{story.name}, {story.age}</p>
                    <p style={{
                      fontSize: isSmallMobile ? '12px' : '13px',
                      color: '#6B7280'
                    }}>{story.location} • {story.condition} • {story.duration}</p>
                  </div>
                </div>

                {/* Stars */}
                <div style={{ display: 'flex', gap: '4px', marginTop: '12px' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#F59E0B" color="#F59E0B" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section style={{
        padding: isSmallMobile ? '60px 16px' : isMobile ? '80px 20px' : '100px 40px',
        background: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #DC2626 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '120px',
          height: '120px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '8%',
          width: '80px',
          height: '80px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite 1s'
        }} />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Tagline */}
          <div style={{
            fontSize: isSmallMobile ? '14px' : '18px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Prevent. Manage. Reverse.
          </div>

          <h2 style={{
            fontSize: isSmallMobile ? '28px' : isMobile ? '36px' : '52px',
            fontWeight: '900',
            color: '#FFFFFF',
            marginBottom: isSmallMobile ? '16px' : '24px',
            lineHeight: '1.15'
          }}>
            Your Healthier Life Starts{' '}
            <span style={{ opacity: 0.9 }}>Today</span>
          </h2>

          <p style={{
            fontSize: isSmallMobile ? '16px' : '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '650px',
            margin: '0 auto',
            marginBottom: isSmallMobile ? '32px' : '40px'
          }}>
            Don't wait for a wake-up call. Join <strong>100,000+ Indians</strong> who've taken 
            control of their health with GEWO's lifestyle disease management programs.
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '16px',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <button style={{
              background: '#FFFFFF',
              color: '#EA580C',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '20px 40px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Target size={22} />
              Begin Your Health Plan
            </button>

            <button style={{
              background: 'transparent',
              color: '#FFFFFF',
              fontSize: isSmallMobile ? '16px' : '18px',
              fontWeight: '700',
              padding: isSmallMobile ? '16px 28px' : '20px 40px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              width: isSmallMobile ? '100%' : 'auto',
              maxWidth: isSmallMobile ? '320px' : 'none',
              justifyContent: 'center',
              minHeight: '56px'
            }}>
              <Phone size={20} />
              Talk to a Health Expert
            </button>
          </div>

          {/* Trust Badges */}
          <div style={{
            display: 'flex',
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? '12px' : '32px',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} color="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                100% Confidential
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={18} color="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                Verified Specialists
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={18} color="white" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: isSmallMobile ? '13px' : '14px' }}>
                Proven Results
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </>
  );
};

export default LifestyleDiseases;
